// ── Firebase config ──────────────────────────────────────────────────────────
// Reads from environment variables (see .env.example). These keys are PUBLIC by
// design — Firebase security is enforced by Firestore Security Rules, not by
// hiding the apiKey. So it's safe to expose them in a client bundle.
//
// IMPORTANT: This module is intentionally "lazy + optional". If the env vars are
// missing (e.g. before you've created your Firebase project), `isFirebaseConfigured`
// is false and the app runs exactly as before — fully offline on localStorage,
// no login gate. Auth/sync only switch on once real config is present.

import { initializeApp } from 'firebase/app';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

// We only need apiKey + projectId + appId to consider it "configured".
export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId
);

let app = null;
let auth = { currentUser: null };
let db = null;

if (isFirebaseConfigured) {
  app = initializeApp(firebaseConfig);
  // Explicit, durable auth persistence. IndexedDB is preferred (survives across
  // reopens even under storage partitioning / ITP on mobile), with a localStorage
  // fallback for browsers where IndexedDB is unavailable. Without this the session
  // can silently fail to persist on some mobile browsers, bouncing the user back
  // to the login screen on every reopen.
  auth = initializeAuth(app, {
    persistence: [indexedDBLocalPersistence, browserLocalPersistence],
    popupRedirectResolver: browserPopupRedirectResolver,
  });
  // Offline-first: cache Firestore data in IndexedDB so the app keeps working
  // with no connection and syncs automatically on reconnect. Multi-tab safe.
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  });
} else if (process.env.NODE_ENV === 'development') {
  // Friendly nudge during local dev — not an error.
  // eslint-disable-next-line no-console
  console.info(
    '[Clarity] Firebase not configured — running in local-only mode. ' +
    'Add REACT_APP_FIREBASE_* vars to .env to enable Google sign-in + sync.'
  );
}

export { app, auth, db };
